import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { HoraHintComponent } from 'src/app/presentation/shared/hora-hint/hora-hint.component';
import { dateToStringTime } from 'src/app/common/date-utils';

const PATRON_HORA_24H = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * Convierte 'HH:mm' (24 horas) a un Date del día de hoy con esa hora.
 * Devuelve null si el texto vacío o no cumple el formato.
 */
export function horaTextoADate(valor: string | null | undefined): Date | null {
  const coincidencia = PATRON_HORA_24H.exec((valor ?? '').trim());
  if (!coincidencia) {
    return null;
  }
  const fecha = new Date();
  fecha.setHours(Number(coincidencia[1]), Number(coincidencia[2]), 0, 0);
  return fecha;
}

/**
 * DUMB COMPONENT: InputHora
 * Campo de hora que siempre se muestra y opera en formato 24 horas (HH:mm),
 * sin depender de la configuración regional del equipo (a diferencia de
 * `<input type="time">`). Integra con formularios (ControlValueAccessor); el valor
 * es un string 'HH:mm' ('' cuando está vacío).
 */
@Component({
  selector: 'app-input-hora',
  standalone: true,
  imports: [FormsModule, DatePickerModule, HoraHintComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputHoraComponent),
      multi: true,
    },
  ],
  template: `
    <p-datepicker
      [inputId]="inputId()"
      [ngModel]="valor()"
      [disabled]="deshabilitado()"
      [timeOnly]="true"
      hourFormat="24"
      [stepMinute]="1"
      [showIcon]="true"
      [placeholder]="placeholder()"
      [fluid]="true"
      styleClass="w-full"
      appendTo="body"
      [showTransitionOptions]="'0ms'"
      [hideTransitionOptions]="'0ms'"
      (ngModelChange)="alCambiar($event)"
      (onBlur)="alPerderFoco()"
    ></p-datepicker>
    <app-hora-hint [id]="idAyuda()" [texto]="textoAyuda()" />
  `,
})
export class InputHoraComponent implements ControlValueAccessor, AfterViewInit {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Id del input interno; enlazarlo con el `for` del label. */
  readonly inputId = input.required<string>();
  readonly placeholder = input<string>('HH:mm');
  readonly textoAyuda = input<string>('Formato 24 horas (HH:mm)');

  protected readonly valor = signal<Date | null>(null);
  protected readonly deshabilitado = signal(false);

  private onChange: (valor: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  protected idAyuda(): string {
    return `${this.inputId()}-ayuda`;
  }

  ngAfterViewInit(): void {
    // p-datepicker no expone aria-describedby: se enlaza la ayuda al input interno.
    const campo = this.host.nativeElement.querySelector<HTMLInputElement>(
      `input[id="${this.inputId()}"]`
    );
    campo?.setAttribute('aria-describedby', this.idAyuda());
  }

  protected alCambiar(fecha: Date | null): void {
    this.valor.set(fecha);
    this.onChange(fecha ? dateToStringTime(fecha) : '');
  }

  protected alPerderFoco(): void {
    this.onTouched();
  }

  writeValue(valor: string | null): void {
    this.valor.set(horaTextoADate(valor));
  }

  registerOnChange(fn: (valor: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(deshabilitado: boolean): void {
    this.deshabilitado.set(deshabilitado);
  }
}
